import { createReducer } from '@reduxjs/toolkit'

import * as authenticationActions from 'redux/actions/Authentication/actions'
import * as actions from 'redux/actions/DataPlatform/connections/CustomEndpoints/actions'
import { CustomEndpoint, DynamicsEntitySet } from 'types/GlobalCustomEndpoint'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

export interface CustomEndpointReducerType {
  data: CustomEndpoint[]
  fetching: boolean
  // dynamics specific
  entitySets: DynamicsEntitySet[]
  /**
   * Which source the entity sets belong to. A plain boolean meant the second
   * Dynamics source you opened kept showing the first one's entity sets.
   */
  entitySetsSourceId: string | null
}

const INITIAL_STATE: CustomEndpointReducerType = {
  data: [],
  fetching: false,
  entitySets: [],
  entitySetsSourceId: null
}

const sortByTableName = (endpoints: CustomEndpoint[]) =>
  [...endpoints].sort((a, b) =>
    sortAlphabeticalAsc(a.attributes.table_name, b.attributes.table_name)
  )

const customEndpointsReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(actions.getAll, (state) => {
      return {
        ...state,
        // Drop the previous source's rows rather than showing them as this
        // source's until the response lands.
        data: [],
        fetching: true
      }
    })
    .addCase(actions.getAllSuccess, (state, action) => {
      const { payload } = action

      return {
        ...state,
        data: sortByTableName(payload.data),
        fetching: false
      }
    })
    .addCase(actions.getAllFailed, (state) => {
      return {
        ...state,
        fetching: false
      }
    })
    .addCase(actions.createSuccess, (state, action) => {
      const { payload } = action

      return {
        ...state,
        data: sortByTableName([...state.data, payload.data])
      }
    })
    .addCase(actions.createManySuccess, (state, action) => {
      const { payload } = action

      return {
        ...state,
        data: sortByTableName([...state.data, ...payload.data])
      }
    })
    .addCase(actions.updateSuccess, (state, action) => {
      const { payload } = action

      return {
        ...state,
        data: sortByTableName(
          state.data.map((item) =>
            (item.id === payload.data.id ? payload.data : item))
        )
      }
    })
    .addCase(actions.destroySuccess, (state, action) => {
      const { payload } = action

      return {
        ...state,
        data: state.data.filter((item) => item.id !== payload.id)
      }
    })
    .addCase(actions.getEntitySetsSuccess, (state, action) => {
      const { payload } = action

      return {
        ...state,
        entitySets: payload.data,
        entitySetsSourceId: payload.activeSourceId
      }
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default customEndpointsReducer
